import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase'; 
import BehaviorTrendChart from './BehaviorTrendChart'; 

const ArcHub = ({ tenantId }) => {
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [studentName, setStudentName] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // View State (Current, Archived, All)
  const [activeView, setActiveView] = useState('current');

  // Raw Firebase Data States
  const [rawProgressData, setRawProgressData] = useState([]);
  const [rawVocationalData, setRawVocationalData] = useState([]);
  const [rawBehaviorData, setRawBehaviorData] = useState([]);

  // Processed Chart Data States
  const [classroomData, setClassroomData] = useState({});
  const [vocationalData, setVocationalData] = useState({});
  const [behaviorComments, setBehaviorComments] = useState([]); 
  const [hasData, setHasData] = useState(false);

  // 1. Initial Load: Fetch Students 
  useEffect(() => {
    let isMounted = true; 
    const fetchStudents = async () => {
      try {
        if (!auth?.currentUser) return;
        
        const userRef = doc(db, 'districts', tenantId, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          if (isMounted) setError("User profile not found.");
          if (isMounted) setLoading(false);
          return;
        }
        const profile = userSnap.data();

        const studentsRef = collection(db, 'districts', tenantId, 'students');
        let studentQuery;
        
        if (profile?.role === 'Case Manager') {
          studentQuery = query(studentsRef, where("assignedCaseManager", "==", auth.currentUser.uid));
        } else if (profile?.role === 'Instructional Assistant') {
          studentQuery = query(studentsRef, where("assignedCaseManager", "==", profile.assignedCaseManager));
        } else if (profile?.role === 'ECE Recorder') {
          studentQuery = query(studentsRef, where("schoolId", "==", profile.schoolId));
        } else {
          studentQuery = query(studentsRef); 
        }
        
        const studentDocs = await getDocs(studentQuery);
        const loadedStudents = studentDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (isMounted) {
          setStudents(loadedStudents.sort((a, b) => (a.name || a.studentName || '').localeCompare(b.name || b.studentName || '')));
        }
      } catch (err) {
        console.error("Error fetching students:", err);
        if (isMounted) setError("Failed to load database.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    fetchStudents();
    return () => { isMounted = false; };
  }, [tenantId]);

  // 2. Fetch Raw Data once when Student is Selected
  useEffect(() => {
    if (!selectedStudentId) {
      setRawProgressData([]);
      setRawVocationalData([]);
      setRawBehaviorData([]);
      setStudentName('');
      return;
    }

    const fetchRawData = async () => {
      setGenerating(true);
      try {
        const targetStudent = students.find(s => s.id === selectedStudentId);
        setStudentName(targetStudent?.name || targetStudent?.studentName || targetStudent?.fullName || 'Student Report');

        const [pSnap, vSnap, bSnap] = await Promise.all([
          getDocs(query(collection(db, 'districts', tenantId, 'students', selectedStudentId, 'progressLogs'))),
          getDocs(query(collection(db, 'districts', tenantId, 'students', selectedStudentId, 'vocationalLogs'))),
          getDocs(query(collection(db, 'districts', tenantId, 'students', selectedStudentId, 'behaviorLogs')))
        ]);

        setRawProgressData(pSnap.docs.map(d => d.data()));
        setRawVocationalData(vSnap.docs.map(d => d.data()));
        setRawBehaviorData(bSnap.docs.map(d => d.data()));
      } catch (err) {
        console.error("Error fetching raw data:", err);
      }
    };

    fetchRawData();
  }, [selectedStudentId, tenantId, students]);

  // 3. Process & Filter Data whenever Raw Data or Active View changes
  useEffect(() => {
    if (!selectedStudentId) {
      setClassroomData({});
      setVocationalData({});
      setBehaviorComments([]);
      setHasData(false);
      return;
    }

    // Time boundaries for chunking
    const now = new Date();
    const oneYearAgoMs = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).getTime();
    const twoYearsAgoMs = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate()).getTime();

    const isWithinView = (timeMs) => {
      if (activeView === 'all') return true;
      if (activeView === 'current') return timeMs >= oneYearAgoMs;
      if (activeView === 'archived') return timeMs >= twoYearsAgoMs && timeMs < oneYearAgoMs;
      return true;
    };

    const extractTimeMs = (data) => {
      if (data.timestamp && data.timestamp.toMillis) return data.timestamp.toMillis();
      if (data.date) return new Date(data.date).getTime();
      return Date.now(); // Fallback
    };

    // --- PROCESS CLASSROOM ---
    const rawClassroom = {};
    rawProgressData.forEach(data => {
      if (!isWithinView(extractTimeMs(data))) return;

      const cat = data.category || 'Uncategorized';
      const goal = data.goalText || 'General Goal';
      const obj = data.objectiveText || 'Primary Goal';
      const score = data.percentage;

      if (score === undefined || score === null) return;

      if (!rawClassroom[cat]) rawClassroom[cat] = {};
      if (!rawClassroom[cat][goal]) rawClassroom[cat][goal] = { objectives: {} };
      if (!rawClassroom[cat][goal].objectives[obj]) rawClassroom[cat][goal].objectives[obj] = { sum: 0, count: 0 };
      
      rawClassroom[cat][goal].objectives[obj].sum += score;
      rawClassroom[cat][goal].objectives[obj].count += 1;
    });

    const finalClassroom = {};
    Object.keys(rawClassroom).forEach(cat => {
      finalClassroom[cat] = {};
      Object.keys(rawClassroom[cat]).forEach(goal => {
        const goalNode = rawClassroom[cat][goal];
        let totalObjAvgSum = 0;
        let objCount = 0;
        const finalObjectives = {};

        Object.keys(goalNode.objectives).forEach(obj => {
          const oData = goalNode.objectives[obj];
          const objAvg = Math.round(oData.sum / oData.count);
          finalObjectives[obj] = objAvg;
          totalObjAvgSum += objAvg;
          objCount += 1;
        });

        const goalAvg = objCount > 0 ? Math.round(totalObjAvgSum / objCount) : 0;
        finalClassroom[cat][goal] = { overallAverage: goalAvg, objectives: finalObjectives };
      });
    });

    // --- PROCESS VOCATIONAL ---
    const rawVocational = {};
    rawVocationalData.forEach(data => {
      if (!isWithinView(extractTimeMs(data))) return;

      const loc = data.siteName || data.location || 'Unknown Work Site';
      if (data.scores && typeof data.scores === 'object') {
        if (!rawVocational[loc]) rawVocational[loc] = {};
        
        Object.entries(data.scores).forEach(([taskName, val]) => {
          let num = Number(val);
          if (!isNaN(num) && num <= 5) {
            if (!rawVocational[loc][taskName]) rawVocational[loc][taskName] = { sum: 0, count: 0 };
            rawVocational[loc][taskName].sum += num;
            rawVocational[loc][taskName].count += 1;
          }
        });
      }
    });

    const finalVocational = {};
    Object.keys(rawVocational).forEach(loc => {
      finalVocational[loc] = {};
      Object.keys(rawVocational[loc]).forEach(task => {
        finalVocational[loc][task] = Math.round((rawVocational[loc][task].sum / rawVocational[loc][task].count) * 10) / 10;
      });
    });

    // --- PROCESS BEHAVIOR COMMENTS ---
    const extractedComments = [];
    rawBehaviorData.forEach(data => {
      const timeMs = extractTimeMs(data);
      if (!isWithinView(timeMs)) return;

      if (data.comment && data.comment.trim() !== '') {
        extractedComments.push({
          sprint: data.sprint?.split(':')[0] || 'Note', 
          text: data.comment,
          time: timeMs
        });
      }
    });

    // CHRONOLOGICAL SORT: Extract the number from "Sprint X" and sort mathematically
    extractedComments.sort((a, b) => {
      const numA = parseInt(a.sprint.replace(/\D/g, ''), 10) || 0;
      const numB = parseInt(b.sprint.replace(/\D/g, ''), 10) || 0;
      return numA - numB;
    });

    // Apply state
    setClassroomData(finalClassroom);
    setVocationalData(finalVocational);
    setBehaviorComments(extractedComments);
    setHasData(Object.keys(finalClassroom).length > 0 || Object.keys(finalVocational).length > 0 || extractedComments.length > 0);
    setGenerating(false);

  }, [rawProgressData, rawVocationalData, rawBehaviorData, activeView, selectedStudentId]);

  // --- UI COMPONENTS ---
  const ProgressBar = ({ label, percentage, color, isObjective = false }) => (
    <div style={{ marginBottom: isObjective ? '10px' : '20px', marginLeft: isObjective ? '20px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <span style={{ fontWeight: isObjective ? 'normal' : 'bold', color: isObjective ? '#555' : '#333', fontSize: isObjective ? '14px' : '16px' }}>
          {isObjective ? '↳ ' : ''}{label}
        </span>
        <span style={{ fontWeight: 'bold', color: color }}>{percentage}%</span>
      </div>
      <div style={{ width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', height: isObjective ? '12px' : '18px', overflow: 'hidden' }}>
        <div style={{ width: `${percentage}%`, backgroundColor: color, height: '100%', borderRadius: '4px' }} />
      </div>
    </div>
  );

  const getScoreColor = (score) => {
    if (score >= 80) return '#28a745'; 
    if (score >= 60) return '#f0ad4e'; 
    return '#ba0c2f'; 
  };

  const RadarChart = ({ data, locationName }) => {
    const tasks = Object.keys(data);
    if (tasks.length === 0) return null;

    const size = 350;
    const center = size / 2;
    const radius = 100; 
    const maxScore = 4;
    const angleStep = (Math.PI * 2) / Math.max(tasks.length, 3); 

    const getPoint = (val, index) => {
      const r = (val / maxScore) * radius;
      const angle = index * angleStep - Math.PI / 2; 
      return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) };
    };

    const renderGrid = () => {
      return [1, 2, 3, 4].map(level => {
        const points = tasks.map((_, i) => getPoint(level, i));
        if (tasks.length < 3) {
           const fakePoints = [0,1,2].map(i => getPoint(level, i));
           return <polygon key={level} points={fakePoints.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#ddd" strokeDasharray={level === 4 ? "0" : "4 4"} />
        }
        return <polygon key={level} points={points.map(p => `${p.x},${p.y}`).join(' ')} fill={level % 2 === 0 ? "rgba(0,0,0,0.02)" : "none"} stroke="#ddd" strokeDasharray={level === 4 ? "0" : "3 3"} />
      });
    };

    const dataPoints = tasks.map((_, i) => getPoint(data[tasks[i]], i));
    const dataPolygonString = tasks.length >= 3 
      ? dataPoints.map(p => `${p.x},${p.y}`).join(' ') 
      : [...dataPoints, {x: center, y: center}].map(p => `${p.x},${p.y}`).join(' '); 

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '40px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #eee' }}>
        <h4 style={{ margin: '0 0 5px 0', color: '#003366', fontSize: '18px' }}>{locationName}</h4>
        <p style={{ margin: '0 0 15px 0', color: '#888', fontSize: '12px' }}>Skill Profile (0-4 Independence Scale)</p>
        
        <svg width="100%" height={size} viewBox={`0 0 ${size} ${size}`} style={{ maxWidth: '400px' }}>
          {renderGrid()}
          {tasks.map((_, i) => {
            const edge = getPoint(4, i);
            return <line key={`axis-${i}`} x1={center} y1={center} x2={edge.x} y2={edge.y} stroke="#eee" strokeWidth="2" />
          })}
          <polygon points={dataPolygonString} fill="rgba(186, 12, 47, 0.2)" stroke="#ba0c2f" strokeWidth="3" />
          {tasks.map((task, i) => {
            const val = data[task];
            const p = getPoint(val, i);
            const labelP = getPoint(4.8, i); 
            
            const angle = i * angleStep - Math.PI / 2;
            const anchor = Math.cos(angle) > 0.1 ? 'start' : Math.cos(angle) < -0.1 ? 'end' : 'middle';

            return (
              <g key={`point-${i}`}>
                <circle cx={p.x} cy={p.y} r="5" fill="#ba0c2f" />
                <text x={p.x + 8} y={p.y - 8} fill="#ba0c2f" fontSize="12" fontWeight="bold">{val}</text>
                <text x={labelP.x} y={labelP.y} textAnchor={anchor} fill="#555" fontSize="11" alignmentBaseline="middle" style={{ maxWidth: '100px' }}>
                  {task.length > 20 ? task.substring(0, 18) + '...' : task}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  };

  const handlePrint = () => { window.print(); };

  // UI Styles for View Buttons
  const btnBaseStyle = { padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s', border: '1px solid' };
  const btnActiveStyle = { ...btnBaseStyle, backgroundColor: '#e6f0fa', color: '#003366', borderColor: '#003366', fontWeight: 'bold' };
  const btnInactiveStyle = { ...btnBaseStyle, backgroundColor: '#f9f9f9', color: '#666', borderColor: '#ddd' };

  const getReportTitle = () => {
    if (activeView === 'archived') return 'Previous Year Progress Report';
    if (activeView === 'all') return 'All Time Progress Report';
    return 'Official ARC Progress Report';
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>Loading ARC Hub...</div>;
  if (error) return <div style={{ padding: '20px', color: '#ba0c2f', fontFamily: 'sans-serif' }}>{error}</div>;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px', fontFamily: 'sans-serif' }}>
      
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            body { background-color: #fff !important; }
            .print-container { box-shadow: none !important; padding: 0 !important; }
            * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
            svg { page-break-inside: avoid; }
          }
        `}
      </style>

      {/* Control Panel */}
      <div className="no-print" style={{ borderBottom: '2px solid #003366', paddingBottom: '20px', marginBottom: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 style={{ margin: 0, color: '#003366', fontSize: '28px' }}>ARC Meeting Hub</h2>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>Automated Progress & Vocational Visualizations</p>
        </div>
        
        {hasData && (
          <button 
            onClick={handlePrint}
            style={{ padding: '12px 20px', backgroundColor: '#ba0c2f', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}
          >
            🖨️ Save as PDF
          </button>
        )}
      </div>

      <div className="no-print" style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
        <div>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Select Student to Generate Report</label>
          <select 
            value={selectedStudentId} 
            onChange={(e) => setSelectedStudentId(e.target.value)}
            style={{ width: '100%', maxWidth: '400px', padding: '12px', borderRadius: '6px', border: '2px solid #003366', fontSize: '16px' }}
          >
            <option value="">-- Choose Student --</option>
            {students.map(s => (
              <option key={s.id} value={s.id}>{s.name || s.studentName || s.fullName || 'Unknown Student'}</option>
            ))}
          </select>
        </div>

        {selectedStudentId && (
          <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Data Timeframe</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button onClick={() => setActiveView('current')} style={activeView === 'current' ? btnActiveStyle : btnInactiveStyle}>📅 Current IEP (1 Year)</button>
              <button onClick={() => setActiveView('archived')} style={activeView === 'archived' ? btnActiveStyle : btnInactiveStyle}>🗃️ Previous Year</button>
              <button onClick={() => setActiveView('all')} style={activeView === 'all' ? btnActiveStyle : btnInactiveStyle}>📈 All Time</button>
            </div>
          </div>
        )}
      </div>

      {generating && <div style={{ textAlign: 'center', padding: '40px', color: '#003366', fontWeight: 'bold' }}>Calculating Averages and Generating Charts...</div>}

      {/* THE PRINTABLE REPORT */}
      {!generating && selectedStudentId && (
        <div className="print-container" style={{ backgroundColor: '#fff', padding: '40px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          
          <div style={{ textAlign: 'center', borderBottom: '2px solid #ccc', paddingBottom: '20px', marginBottom: '30px' }}>
            <h1 style={{ margin: '0 0 10px 0', fontSize: '32px', color: '#003366' }}>{studentName}</h1>
            <h3 style={{ margin: 0, color: '#666' }}>{getReportTitle()}</h3>
            <p style={{ margin: '5px 0 0 0', color: '#999', fontSize: '14px' }}>Generated on: {new Date().toLocaleDateString()}</p>
          </div>

          {!hasData ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666', fontStyle: 'italic' }}>
              No data has been logged for this timeframe.
            </div>
          ) : (
            <>
              {/* SECTION 1: CLASSROOM PROGRESS (Bar Charts) */}
              {Object.keys(classroomData).length > 0 && (
                <div style={{ marginBottom: '50px' }}>
                  <h2 style={{ backgroundColor: '#f4f7f6', padding: '10px 15px', color: '#003366', borderRadius: '6px', fontSize: '20px', borderLeft: '5px solid #003366' }}>
                    📚 Classroom IEP Goals
                  </h2>
                  <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px', marginLeft: '15px' }}>
                    *Goal scores are an average of their underlying objectives (0-100%).
                  </p>
                  
                  {Object.entries(classroomData).map(([category, goals]) => (
                    <div key={category} style={{ marginBottom: '30px', paddingLeft: '15px' }}>
                      <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '5px', color: '#444' }}>{category}</h3>
                      
                      {Object.entries(goals).map(([goalText, goalData]) => {
                        const objKeys = Object.keys(goalData.objectives);
                        const isPrimaryOnly = objKeys.length === 1 && objKeys[0] === 'Primary Goal';

                        return (
                          <div key={goalText} style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#fcfcfc', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
                            <ProgressBar 
                              label={isPrimaryOnly ? goalText : `GOAL: ${goalText}`} 
                              percentage={goalData.overallAverage} 
                              color={getScoreColor(goalData.overallAverage)} 
                            />
                            
                            {!isPrimaryOnly && (
                              <div style={{ marginTop: '15px', borderTop: '1px dashed #ddd', paddingTop: '15px' }}>
                                {Object.entries(goalData.objectives).map(([objText, objAvg]) => (
                                  <ProgressBar 
                                    key={objText}
                                    label={objText} 
                                    percentage={objAvg} 
                                    color={'#6c757d'} 
                                    isObjective={true} 
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {/* SECTION 2: VOCATIONAL PROGRESS (Radar Charts) */}
              {Object.keys(vocationalData).length > 0 && (
                <div style={{ marginBottom: '50px' }}>
                  <h2 style={{ backgroundColor: '#fdf4f5', padding: '10px 15px', color: '#ba0c2f', borderRadius: '6px', fontSize: '20px', borderLeft: '5px solid #ba0c2f' }}>
                    🏢 Vocational & Community Readiness
                  </h2>
                  <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px', marginLeft: '15px' }}>
                    *Skill profiles reflect task independence on a 0-4 Prompt Scale.
                  </p>
                  
                  <div style={{ padding: '20px', backgroundColor: '#fcfcfc', border: '1px solid #f0f0f0', borderRadius: '8px', marginLeft: '15px', display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
                    {Object.entries(vocationalData).map(([location, taskData]) => (
                      <RadarChart 
                        key={location} 
                        locationName={location} 
                        data={taskData} 
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION 3: GENERAL EDUCATION BEHAVIOR */}
              <div style={{ marginTop: '30px' }}>
                <h2 style={{ backgroundColor: '#e6f0fa', padding: '10px 15px', color: '#0056b3', borderRadius: '6px', fontSize: '20px', borderLeft: '5px solid #0056b3' }}>
                  📊 General Education Behavior Trends
                </h2>
                
                <div style={{ marginLeft: '15px', marginTop: '20px' }}>
                  {/* Passing timeRange to let the chart component filter its own query if needed */}
                  <BehaviorTrendChart 
                    tenantId={tenantId} 
                    studentId={selectedStudentId} 
                    timeRange={activeView}
                  />
                </div>

                {behaviorComments.length > 0 && (
                  <div style={{ marginLeft: '15px', marginTop: '40px' }}>
                    <h3 style={{ fontSize: '16px', color: '#444', borderBottom: '2px solid #eee', paddingBottom: '8px', marginBottom: '20px' }}>
                      💬 Anecdotal Teacher Notes
                    </h3>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      {behaviorComments.map((note, idx) => (
                        <div key={idx} style={{ backgroundColor: '#fcfcfc', border: '1px solid #e0e0e0', borderLeft: '4px solid #0056b3', padding: '15px', borderRadius: '6px' }}>
                          <span style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#0056b3', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {note.sprint}
                          </span>
                          <span style={{ fontSize: '14px', color: '#555', fontStyle: 'italic', lineHeight: '1.5' }}>
                            "{note.text}"
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Report Footer */}
          {hasData && (
            <div style={{ marginTop: '50px', paddingTop: '20px', borderTop: '2px solid #ccc', textAlign: 'center', color: '#999', fontSize: '12px' }}>
              CaseSync OS • Official Data Export
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default ArcHub;